import type { ReactNode } from 'react';
import { Alert, Flex, Text } from '@backstage/ui';

export type ExplainTone = 'info' | 'warning' | 'success';

export interface ExplainProps {
  /** Short headline, e.g. "We couldn't check who can open this app". */
  title: ReactNode;
  /** What we actually know right now. */
  whatWeKnow?: ReactNode;
  /** Why the value might be blank/unknown — the educational part. */
  why?: ReactNode;
  /** The concrete next step, including who to ask. */
  whatToDo?: ReactNode;
  tone?: ExplainTone;
}

/**
 * The "always clarify the fallback" primitive for the non-engineer page.
 *
 * Every empty / unknown / error state on this page renders an `<Explain>`
 * instead of a bare warning icon or an em dash, so the reader always gets:
 * what we know · why it might be blank · what to do next (incl. who to ask).
 */
export const Explain = ({
  title,
  whatWeKnow,
  why,
  whatToDo,
  tone = 'info',
}: ExplainProps) => {
  return (
    <Alert
      status={tone}
      icon
      title={title}
      description={
        whatWeKnow || why || whatToDo ? (
          <Flex direction="column" gap="1" mt="1">
            {whatWeKnow && <Text variant="body-medium">{whatWeKnow}</Text>}
            {why && (
              <Text variant="body-medium" color="secondary">
                {why}
              </Text>
            )}
            {whatToDo && (
              <Text variant="body-medium">
                <Text as="strong" variant="body-medium" weight="bold">
                  What to do:{' '}
                </Text>
                {whatToDo}
              </Text>
            )}
          </Flex>
        ) : undefined
      }
    />
  );
};

/** Where non-engineers should go when something is unclear. */
export const ASK_DEVPROD = '#ask-devprod';
